import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import { PanelCard, PanelFooter, Section, TextField, InfoRow, useReportDirty } from './controls';

export default function OverlayPanel({ config, onSave, loading, onDirtyChange }: any) {
  const [channelName, setChannelName] = useState('');

  const reset = () => setChannelName(config?.channel_name || '');
  useEffect(reset, [config]);

  const dirty = channelName !== (config?.channel_name || '');
  useReportDirty(dirty, onDirtyChange);

  const datetimeOn = !!config?.datetime_enabled;
  const dateFmt = config?.datetime_format || 'yyyy-MM-dd';
  const timeFmt = config?.time_format || 'HH:mm:ss';

  return (
    <PanelCard
      title="Overlay / OSD"
      description="Text the camera burns into the video itself, before it reaches the recorder."
      footer={
        <PanelFooter
          dirty={dirty} loading={loading} onSave={() => onSave({ channel_name: channelName })} onReset={reset}
          dirtyLabel="Unsaved channel name"
        />
      }
    >
      <Section title="Channel Name" collapsible={false}
        hint="Appears bottom-left in the encoded stream and in exported clips.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="Text" value={channelName} placeholder="e.g. Front Door"
            onChange={setChannelName} disabled={loading} />
          <InfoRow label="Position (set on the camera)" value={config?.channel_position} />
        </div>
      </Section>

      <Section title="Date & Time Overlay" collapsible={false}
        hint="Read-only — this camera exposes the timestamp format but not a way to change it here.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <p className="text-[11px] font-medium text-muted-foreground mb-1">Status</p>
            <p className={`flex items-center gap-1.5 text-[13px] font-medium ${datetimeOn ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
              {datetimeOn ? <CheckCircleIcon className="h-4 w-4" /> : <MinusCircleIcon className="h-4 w-4" />}
              {datetimeOn ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          <InfoRow label="Date Format" mono value={config?.datetime_format} />
          <InfoRow label="Time Format" mono value={config?.time_format} />
        </div>
      </Section>

      {/* Layout sketch, not a frame grab — the live feed is already on this page, so the value
          here is showing where the text lands, which the real preview can't do for unsaved edits. */}
      <Section title="Layout" collapsible={false} hint="Where the overlays sit in the frame.">
        <div className="relative aspect-video max-w-sm overflow-hidden rounded-lg border border-border bg-muted">
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="border border-border" />
            ))}
          </div>
          <div className="absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-[11px] font-medium text-white">
            {channelName || 'Channel Name'}
          </div>
          {datetimeOn && (
            <div className="absolute right-2 top-2 rounded bg-black/70 px-2 py-1 text-[11px] font-medium tabular-nums text-white">
              {dateFmt} {timeFmt}
            </div>
          )}
        </div>
      </Section>
    </PanelCard>
  );
}
